/* ============================================================
 * src/screens/content/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-content.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired; the role-mirror permission checks
// in this bundle bind to the canonical `can()` helper, fed by the real
// `useCurrentMember()` hook. No fixture dependency remains.
import { can as canPerm } from '../../lib/permissions';
import { useCurrentMember } from '../../hooks/useCurrentMember';
import { fmtDur, ago } from '../../lib/format';
import { navigate } from '../../lib/nav';
import { AssetPlayer } from '../pending/_player';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { MockBoundary } from '../../components/Provenance';

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — Audio & Podcast (v.1-D) + Publishing capacity
   H1 audio-version · H2 podcast settings · H3 feed health · J1 capacity
   Honest lifecycle states; deep-green; Spanish; friendly labels only.
   ============================================================ */

/* Per-asset audio rendition lifecycle (deterministic by id).
 * NOTE: this is SIMULATED data — it fabricates the rendition state/url/etc.
 * from a seed and is NOT wired to the real `assets.audioRendition` backend
 * field. The tab is flagged with a SIM provenance badge. The id read is
 * guarded because the real Convex asset exposes `_id`, not `id`. */
function audioRenditionFor(a) {
  const id = String(a?._id ?? a?.id ?? '');
  const seed = id.length ? (id.charCodeAt(id.length - 1) + id.length) % 4 : 0;
  const state = (a?.status ?? a?.transcodingStatus) !== 'ready' && (a?.transcodingStatus !== 'completed') ? 'pending' : ['published', 'published', 'processing', 'failed'][seed];
  return {
    state,
    format: seed === 1 ? 'M4A · AAC' : 'MP3',
    bitrate: seed === 1 ? '256 kbps' : '128 kbps',
    durationMs: a.durationMs,
    sizeMB: Math.max(1, Math.round((a.durationMs / 1000) * (seed === 1 ? 0.032 : 0.016) * 10) / 10),
    publishedAt: Date.now() - (seed + 1) * 3600_000 * 6,
    reason: 'El archivo de origen no contiene una pista de audio legible.',
    url: `https://audio.circa.tv/v/${id}/audio.${seed === 1 ? 'm4a' : 'mp3'}`,
  };
}

/* ---------- H1 — Audio-version tab (rendered inside ContentDetail) ---------- */
export function AudioVersionTab({ asset }) {
  const { toast } = useToast();
  const member = useCurrentMember();
  const [r, setR] = useState(() => audioRenditionFor(asset));
  const tone = { published: 'good', processing: 'warn', pending: 'neutral', failed: 'bad' }[r.state];
  const label = { published: 'Publicado', processing: 'Procesando', pending: 'Pendiente', failed: 'Falló' }[r.state];

  const publish = () => {
    if (!canPerm('podcast_publish', member?.role)) return;
    setR(x => ({ ...x, state: 'processing' }));
    toast({ variant: 'info', message: 'Publicación de audio en cola. Te avisaremos cuando termine.' });
    setTimeout(() => { setR(x => ({ ...x, state: 'published', publishedAt: Date.now() })); toast({ variant: 'success', message: 'Versión de audio publicada.' }); }, 2600);
  };
  const retry = () => {
    if (!canPerm('audio_retry', member?.role)) return;
    setR(x => ({ ...x, state: 'processing' }));
    toast({ variant: 'info', message: 'Reintentando la extracción de audio…' });
    setTimeout(() => { setR(x => ({ ...x, state: 'published', publishedAt: Date.now() })); toast({ variant: 'success', message: 'Versión de audio publicada.' }); }, 2600);
  };

  return (
    <MockBoundary detail="Toda esta pestaña es simulada: estado, formato, bitrate, tamaño y URL se fabrican desde una semilla — no provienen del campo real assets.audioRendition.">
    <Panel title="Versión de audio" right={<Pill tone={tone} dot={r.state === 'processing'}>{label}</Pill>}>
      {r.state === 'pending' && (
        <div>
          <EmptyState icon="mic" title="Aún no hay versión de audio" body="Convierte el audio de este contenido en una versión publicable y compartible. Se codifica una sola vez y queda disponible como enclosure seguro." />
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
            <AccessGate perm="podcast_publish"><Btn variant="solid" icon="send" onClick={publish}>Publicar versión de audio</Btn></AccessGate>
          </div>
        </div>
      )}

      {r.state === 'processing' && (
        <div style={{ padding: '8px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <Icon name="loader" size={16} color="var(--warn)" style={{ animation: 'circa-spin 0.8s linear infinite' }} />
            <span style={{ fontSize: 14, color: 'var(--body-muted)' }}>Procesando la versión de audio. Esto puede tardar unos minutos.</span>
          </div>
          <Progress value={62} color="var(--warn)" />
          <p style={{ margin: '14px 0 0', fontSize: 13, color: 'var(--muted-slate)' }}>No mostraremos un enlace público hasta que la versión se publique de verdad.</p>
        </div>
      )}

      {r.state === 'failed' && (
        <div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: 16, background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 14, marginBottom: 16 }}>
            <Icon name="alertCircle" size={18} color="var(--bad)" style={{ marginTop: 1 }} />
            <div style={{ flex: 1 }}><div style={{ fontWeight: 500, marginBottom: 4 }}>No se pudo publicar la versión de audio</div><p style={{ margin: 0, fontSize: 13, color: 'var(--body-muted)' }}>{r.reason}</p></div>
          </div>
          <AccessGate perm="audio_retry"><Btn variant="ghost" icon="refresh" onClick={retry}>Reintentar</Btn></AccessGate>
        </div>
      )}

      {r.state === 'published' && (
        <div>
          <div style={{ marginBottom: 18 }}>
            <AssetPlayer descriptor={{ assetId: asset.id, mediaKind: 'audio', source: { url: r.url, contentType: 'audio/mpeg' }, title: asset.title, durationMs: r.durationMs, textTracks: [] }} />
          </div>
          <div className="circa-grid-4" style={{ marginBottom: 18 }}>
            {[['Formato', r.format], ['Tasa de bits', r.bitrate], ['Duración', fmtDur(r.durationMs)], ['Tamaño', r.sizeMB + ' MB']].map(([k, v]) => (
              <div key={k} style={{ background: 'var(--snow)', borderRadius: 14, padding: 14 }}>
                <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginBottom: 6 }}>{k}</div>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{v}</div>
              </div>
            ))}
          </div>
          {/* inset forest-green delivery card */}
          <div style={{ borderRadius: 16, padding: 16, background: 'linear-gradient(135deg, var(--forest-a), var(--forest-b))', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <Icon name="linkIcon" size={18} color="rgba(255,255,255,0.8)" />
            <code style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 13, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.url}</code>
            <Btn variant="onDark" size="sm" icon="copy" onClick={() => toast({ variant: 'success', message: 'Enlace de audio copiado.' })}>Copiar enlace de audio</Btn>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>Publicado {ago(r.publishedAt)} · entrega segura</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <AccessGate perm="audio_retry"><Btn size="sm" variant="ghost" icon="refresh" onClick={retry}>Re-publicar</Btn></AccessGate>
            </div>
          </div>
        </div>
      )}
    </Panel>
    </MockBoundary>
  );
}

/* ---------- H2 — Podcast settings — REMOVED (NMW-457) ----------
 * `PodcastSettingsPanel` was an unmarked fake: `enabled` was seeded from the
 * retired mock ids ch_1/ch_5, the author defaulted to the literal 'Canal Circa
 * S.A.', the public feed URLs were templated against a non-existent
 * feeds.circa.tv host and save() only raised a success toast. No podcast field
 * exists on the `channels` schema and no mutation writes one. Its only importer
 * was the channel Ajustes rail, whose «Pódcast» entry was removed with it. */

/* ---------- H3 — Feed health ---------- */
export function FeedHealth() {
  const { toast } = useToast();
  // Podcast-enabled channels surface here once the backend exposes a
  // podcast flag on `channels`. The current schema (convex/schema.ts §
  // channels) has no podcast / RSS-feed field, so there is no honest way
  // to compute "podcast-enabled channels" from real data. We still
  // subscribe to `listChannels` so the loading vs. empty states behave
  // correctly when the flag eventually lands; until then `podcasts` is
  // an empty array and the EmptyState below tells the operator to enable
  // podcasting in a channel's settings.
  const channels = useQuery(api.channels.listChannels, {}) as
    | Array<{ _id: string; name: string; primaryColor?: string }>
    | undefined;
  const loading = channels === undefined;
  const podcasts: Array<{
    id: string;
    name: string;
    primaryColor: string;
    feedState: 'pass' | 'validating';
    pass: number;
    fail: number;
    lastChecked: number | null;
    episodes: number;
  }> = [];
  return (
    <div>
      <PageHead title="Salud del feed" description="Confirma que tus feeds públicos de pódcast son válidos antes de que los escuchen tus oyentes." />
      <div style={{ padding: 16, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 14, marginBottom: 24, fontSize: 13, color: 'var(--body-muted)', display: 'flex', gap: 10 }}>
        <Icon name="info" size={16} color="var(--warn)" />Un validador diario revisa la estructura de cada feed. No marcamos un feed como válido hasta que pase una verificación real.
      </div>
      {loading ? <SkeletonList rows={2} /> : (
        podcasts.length === 0 ? <Panel><EmptyState icon="radio" title="Ningún canal es pódcast todavía" body="Habilita el pódcast en los ajustes de un canal para empezar a publicar un feed de audio." action={<Btn variant="solid" onClick={() => navigate('/channels')}>Ir a canales</Btn>} /></Panel> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {podcasts.map(p => (
            <Panel key={p.id} pad={20}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 52, height: 52, borderRadius: 12, background: `linear-gradient(135deg, ${p.primaryColor}, var(--forest-a))`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="mic" size={22} color="#fff" /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500 }}>{p.name}</span>
                    {p.feedState === 'pass'
                      ? <Pill tone="good" dot>Válido</Pill>
                      : <Pill tone="warn" dot>Validándose</Pill>}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted-slate)' }}>
                    {p.feedState === 'pass'
                      ? `${p.episodes} episodios · ${p.pass} verificaciones superadas · revisado ${ago(p.lastChecked)}`
                      : 'Aún sin primera verificación. El validador se ejecuta a diario.'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn size="sm" variant="ghost" icon="externalLink" onClick={() => toast({ variant: 'info', message: 'Abriendo feed RSS.' })}>Ver feed</Btn>
                  {/*
                   * T328 (FR-005): the validate action lives at
                   * `api.ingestSources.validate({ payloadId })`. This podcast-channel
                   * view does not yet carry a `payloadId` per row (the mapping from
                   * a podcast-enabled channel to its Payload ingest-source row is a
                   * separate feature). Per /feed-sources is the wired surface for
                   * the validate flow today; this button surfaces an honest
                   * "pendiente" message rather than fabricating a success.
                   */}
                  <Btn size="sm" variant="ghost" icon="refresh" decorative onClick={() => toast({ variant: 'info', message: 'Validación de feed de pódcast aún no disponible — usa la pantalla de fuentes para validar fuentes editoriales.' })}>Validar ahora</Btn>
                </div>
              </div>
            </Panel>
          ))}
        </div>
        )
      )}
    </div>
  );
}

/* ---------- J1 — Publishing capacity (honest deferral) ---------- */
/**
 * T333 (FR-013) — `/publishing-capacity` setTimeout theatre REMOVED.
 *
 * The previous implementation:
 *   - Held a local `useState` pool with hardcoded numbers (4200/9200/1100/0).
 *   - On "Rotar capacidad", fired `setTimeout(...1400ms)` and then a
 *     `toast.success('Capacidad rotada')` — the canonical FR-013 anti-
 *     pattern: a green success toast for an action that PERSISTED NOTHING.
 *   - Backed the "Historial de rotación" panel with a tuple literal.
 *
 * Why it cannot just be wired to a real mutation:
 *   - `convex/publishing.ts` exports exactly ONE write — `rotateQuotaProject`
 *     — and its handler is the canonical `stubError("publishing.rotateQuotaProject")`
 *     (verified at `convex/publishing.ts:7`). Calling it through
 *     `useMutationWithToast` would re-throw immediately and surface a red
 *     toast — that is the correct honest behaviour, but it gives the
 *     operator no useful affordance.
 *   - The real per-project read (`api.queries.youtubeApiProjects.listPublic`,
 *     landed by T333 in lockstep) is now exposed on `/destinations`; the
 *     workspace-scoped capacity panel on this surface is redundant once
 *     the destinations page is the canonical view.
 *
 * Honest-state choice (per FR-013 + NFR-004): render an EmptyState that:
 *   - Tells the operator the manual rotation lives in the destinations
 *     dashboard (where the per-project quota is real and live).
 *   - Acknowledges that the manual workspace rotation control is still
 *     pending (`próximamente`) — never green-toast a non-persisting
 *     action, never fabricate pool data.
 *
 * The `AdminGate` wrapper stays — the route remains admin-only, so a
 * viewer attempting to reach it still sees the gate's redirect.
 */
export function PublishingCapacity() {
  return (
    <AdminGate>
      <PageHead
        title="Capacidad de publicación"
        description="Distribuye las publicaciones entre varias reservas de capacidad para que la entrega nunca se detenga por un límite."
      />
      <Panel>
        <EmptyState
          icon="server"
          title="Próximamente — Rotación manual de capacidad"
          body="La rotación manual de las reservas de publicación llegará en una próxima entrega. Mientras tanto, los proyectos de la API de YouTube se rotan automáticamente y puedes revisar su estado actual en la página de destinos."
          action={
            <Btn variant="ghost" iconRight="arrowRight" onClick={() => navigate('/destinations')}>
              Ir a destinos
            </Btn>
          }
        />
      </Panel>
    </AdminGate>
  );
}

